import { useState, FormEvent } from 'react';
import { navigate } from '@/hooks/useRouter';
import { usePageContext } from '@/hooks/useRouter';
import { Eye, EyeOff, User, Lock } from 'lucide-react';
import NavBar from '@/components/ui/NavBar';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import Checkbox from '@/components/ui/Checkbox';
import AlertDialog from '@/components/ui/AlertDialog';
import { useSiteConfig, useAuth, useToast } from '@/contexts/GlobalProvider';

export default function Page() {
  const { config } = useSiteConfig();
  const { login: authLogin } = useAuth();
  const toast = useToast();
  const pageContext = usePageContext();
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  // 待审核提示对话框
  const [pendingDialogOpen, setPendingDialogOpen] = useState(false);

  // 获取 redirect 参数
  const redirectUrl = (pageContext as any).urlParsed?.search?.redirect as string | undefined;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!username || !password) {
      toast.warning('请输入账号和密码');
      return;
    }

    setLoading(true);
    try {
      // 使用 AuthContext 的 login 方法
      await authLogin({
        account: username,
        password: password,
      });

      if (remember) {
        localStorage.setItem('rememberLogin', 'true');
      }

      toast.success('登录成功');

      // 登录成功后跳转到 redirect 参数指定的页面或首页（使用 replace 避免返回到登录页）
      navigate(redirectUrl || '/', { overwriteLastHistoryEntry: true });
    } catch (error: any) {
      // 待审核状态显示对话框
      if (error.code === 'UserPendingReview') {
        setPendingDialogOpen(true);
      }
      // 其他错误由 AuthContext 处理
    } finally {
      setLoading(false);
    }
  };

  const passwordToggle = (
    <button
      type="button"
      onClick={() => setShowPassword((prev) => !prev)}
      className="text-white/60 hover:text-white transition-colors"
      aria-label={showPassword ? '隐藏密码' : '显示密码'}
    >
      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  );

  return (
    <div className="min-h-screen flex flex-col bg-bg-page">
      <NavBar
        title={`${config?.clientName || '用户端'}登录`}
        className="bg-white border-b border-border-color"
      />

      <div className="flex-1 flex flex-col items-center px-4 pt-20 pb-8">
        <div className="w-24 h-24 rounded-full overflow-hidden mb-4 shadow-md p-3">
          <img
            src="/public/icon.png"
            alt={config?.clientName || '用户端'}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <h2 className="text-xl font-semibold text-text-primary mb-8">
          欢迎登录 {config?.clientName || '用户端'}
        </h2>

        <div className="w-full max-w-md bg-white rounded-2xl p-6 shadow-md">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                placeholder="请输入账号"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                leftIcon={<User size={18} />}
                clearable
                onClear={() => setUsername('')}
                required
              />
            </div>

            <div>
              <Input
                placeholder="请输入密码"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                leftIcon={<Lock size={18} />}
                rightIcon={passwordToggle}
                clearable
                onClear={() => setPassword('')}
                required
              />
            </div>

            <div className="flex items-center">
              <Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} shape="round">
                记住登录状态
              </Checkbox>
            </div>

            <Button type="submit" variant="primary" block round disabled={loading}>
              {loading ? '登录中...' : '登录'}
            </Button>
            <Button variant="ghost" block round onClick={() => navigate('/register')}>
              立即注册
            </Button>
          </form>
        </div>

        <div className="w-full max-w-md mt-6 space-y-3">
          {config?.appDownloadUrl && (
            <a href={config.appDownloadUrl} target="_blank" rel="noopener noreferrer">
              <Button
                variant="default"
                block
                round
                className="flex items-center justify-center gap-2 bg-white text-text-primary border border-border-color"
              >
                <span>⬇</span>
                下载 APP
              </Button>
            </a>
          )}
        </div>
      </div>

      {/* 待审核提示对话框 */}
      <AlertDialog
        open={pendingDialogOpen}
        onOpenChange={setPendingDialogOpen}
        type="info"
        title="账号审核中"
        description="您的账号正在审核中，请耐心等待管理员审核通过后再登录。如有疑问请联系客服。"
        confirmText="我知道了"
      />
    </div>
  );
}
